import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiTarget } from "react-icons/fi";
import { DEFAULT_MARKS } from "../../video/puppet";

const HANDLES = [
  { key: "le", label: "Left eye", short: "L eye", color: "#22d3ee" },
  { key: "re", label: "Right eye", short: "R eye", color: "#22d3ee" },
  { key: "ml", label: "Left corner of the mouth", short: "Mouth L", color: "#f472b6" },
  { key: "mr", label: "Right corner of the mouth", short: "Mouth R", color: "#f472b6" },
];

// Asks the browser to find the face, when it can (Chrome's face detector). Returns marks or null.
export async function detectFace(img) {
  try {
    if (typeof window === "undefined" || !("FaceDetector" in window)) return null;
    const faces = await new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 }).detect(img);
    const landmarks = faces[0]?.landmarks || [];
    const eyes = landmarks.filter((l) => l.type === "eye").map((l) => l.locations.reduce((a, p) => [a[0] + p.x / l.locations.length, a[1] + p.y / l.locations.length], [0, 0])).sort((a, b) => a[0] - b[0]);
    const mouth = landmarks.find((l) => l.type === "mouth");
    if (eyes.length < 2 || !mouth) return null;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const mx = mouth.locations.reduce((a, p) => a + p.x, 0) / mouth.locations.length;
    const my = mouth.locations.reduce((a, p) => a + p.y, 0) / mouth.locations.length;
    const half = Math.max(0.28 * Math.abs(eyes[1][0] - eyes[0][0]), 6);
    const norm = (x, y) => [Math.min(1, Math.max(0, x / w)), Math.min(1, Math.max(0, y / h))];
    return { le: norm(...eyes[0]), re: norm(...eyes[1]), ml: norm(mx - half, my), mr: norm(mx + half, my) };
  } catch {
    return null;
  }
}

// The photo, with four handles to drag onto the eyes and the corners of the mouth. The lips are moved from these points.
export default function FaceMarker({ src, marks, onChange }) {
  const boxRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [active, setActive] = useState("le");
  const value = marks || DEFAULT_MARKS;
  const latest = useRef(value);                 // the newest marks, so a drag never works from stale ones
  latest.current = value;

  const move = useCallback((key, clientX, clientY) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    onChange({ ...latest.current, [key]: [Math.round(x * 10000) / 10000, Math.round(y * 10000) / 10000] });
  }, [onChange]);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => move(dragging, e.clientX, e.clientY);
    const onUp = () => setDragging(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [dragging, move]);

  const nudge = (key, e) => {
    const step = e.shiftKey ? 0.01 : 0.003;
    const [x, y] = latest.current[key];
    const next = { ArrowLeft: [x - step, y], ArrowRight: [x + step, y], ArrowUp: [x, y - step], ArrowDown: [x, y + step] }[e.key];
    if (!next) return;
    e.preventDefault();
    onChange({ ...latest.current, [key]: [Math.min(1, Math.max(0, next[0])), Math.min(1, Math.max(0, next[1]))] });
  };

  return (
    <div>
      <div ref={boxRef} className="relative inline-block max-w-full select-none touch-none rounded-xl overflow-hidden border border-navy-border bg-[conic-gradient(#2a2f3a_25%,#222732_0_50%,#2a2f3a_0_75%,#222732_0)] bg-[length:20px_20px]">
        <img src={src} alt="Your photo: drag the dots onto the eyes and the corners of the mouth" draggable={false} className="block max-h-[460px] max-w-full w-auto h-auto" />
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <line x1={value.le[0] * 100} y1={value.le[1] * 100} x2={value.re[0] * 100} y2={value.re[1] * 100} stroke="#22d3ee" strokeWidth="0.35" strokeDasharray="1.2 1" vectorEffect="non-scaling-stroke" opacity="0.8" />
          <line x1={value.ml[0] * 100} y1={value.ml[1] * 100} x2={value.mr[0] * 100} y2={value.mr[1] * 100} stroke="#f472b6" strokeWidth="0.35" strokeDasharray="1.2 1" vectorEffect="non-scaling-stroke" opacity="0.8" />
        </svg>
        {HANDLES.map((h) => (
          <button key={h.key} type="button" aria-label={`${h.label}: use the arrow keys or drag`} title={h.label}
            onPointerDown={(e) => { e.preventDefault(); setActive(h.key); setDragging(h.key); }} onFocus={() => setActive(h.key)} onKeyDown={(e) => nudge(h.key, e)}
            className={`absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full border-2 flex items-center justify-center cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-white ${active === h.key ? "scale-110" : ""}`}
            style={{ left: `${value[h.key][0] * 100}%`, top: `${value[h.key][1] * 100}%`, borderColor: h.color, background: "rgba(8,12,22,0.55)" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: h.color }} />
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-300">
        {HANDLES.map((h) => <span key={h.key} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: h.color }} />{h.short}</span>)}
        <span className="inline-flex items-center gap-1.5 text-gray-500"><FiTarget size={12} /> centre of each eye, and the two corners of the closed mouth</span>
      </div>
    </div>
  );
}
